// Hlášení k aplikaci — závady a náměty (F12 12.5; kontrakt tag Podpora, C3 `hlaseni`; 08 §3.18: `hlaseni.podat`
// každý přihlášený, `hlaseni.cist_vlastni` jen své, `hlaseni.resit` administrátor — jednatel sekci nemá):
//   GET  /hlaseni?typ=&stav=&zdroj=&sekce=&jen_moje=&hledat=&razeni=&strana=&velikost=
//                                     admin všechna, ostatní svá; `stav=otevrene` = nové + řeší se; `pocty` podle stavu
//                                     (+ `otevrene`) pro čipy (nezávisle na filtru)
//   POST /hlaseni                     podat: text, kde, sekce a adresa obrazovky, verze aplikace; přílohy se nahrávají
//                                     předem s `entita_id = klient_uuid` (stejně jako fotka u hlášení z provozu)
//   GET  /hlaseni/{id}                detail s přílohami (podepsané odkazy)
//   POST /hlaseni/{id}/resit          admin: stav (resi_se | vyreseno | zamitnuto) + odpověď hlásícímu; zamítnutí
//                                     má odpověď povinnou; nepovolený přechod 409 `stav.neplatny_prechod`
// Stavy, přechody a popisky jsou stejné jako na serveru (backend/src/moduly/hlaseni/typy.ts) — UI jen vysvětluje,
// rozhoduje server.
import type { Schema } from '@ecoser/shared';
import { keepPreviousData, useQuery, type QueryClient } from '@tanstack/react-query';
import { api } from './klient';

export type Hlaseni = Schema<'Hlaseni'> & { id: string };
export type HlaseniSeznam = Omit<Schema<'HlaseniSeznam'>, 'polozky'> & { polozky: Hlaseni[] };
export type HlaseniVstup = Schema<'HlaseniVstup'>;
export type TypHlaseni = Hlaseni['typ'];
export type StavHlaseni = Hlaseni['stav'];
export type ZdrojHlaseni = Hlaseni['zdroj'];
export type PrioritaHlaseni = NonNullable<Hlaseni['priorita']>;

export interface ResitVstup {
  stav: Exclude<StavHlaseni, 'nove'>;
  odpoved?: string;
  priorita?: PrioritaHlaseni;
}

export const AKCE_PODAT = 'hlaseni.podat';
export const AKCE_CIST_VLASTNI = 'hlaseni.cist_vlastni';
export const AKCE_RESIT = 'hlaseni.resit';

export const KLIC_HLASENI = ['hlaseni'] as const;

/** Kolik vlastních hlášení ukazuje blok na Domů a drawer Nahlásit. */
export const MOJE_HLASENI_POCET = 8;

export const STAVY_HLASENI: readonly StavHlaseni[] = ['nove', 'resi_se', 'vyreseno', 'zamitnuto'];

/** Pohled na frontu: `otevrene` = nové + řeší se (výchozí), `vse` = bez filtru stavu (zadavatel 30. 10. 2026). */
export type PohledHlaseni = 'otevrene' | StavHlaseni | 'vse';
export const POHLEDY_HLASENI: readonly PohledHlaseni[] = ['otevrene', ...STAVY_HLASENI, 'vse'];

/** Stav hlášení, který je uzavřený (vyřešeno, zamítnuto) — karta má jen „Otevřít znovu“. */
export const jeUzavrene = (stav: StavHlaseni): boolean =>
  stav === 'vyreseno' || stav === 'zamitnuto';

/** Prototyp `HLASENI_STAV_META` H:43488 (nové červeně, řeší se oranžově, vyřešeno zeleně, zamítnuto šedě). */
export const STITEK_STAVU: Readonly<Record<StavHlaseni, string>> = {
  nove: 'stitek--danger',
  resi_se: 'stitek--warn',
  vyreseno: 'stitek--ok',
  zamitnuto: 'stitek--neutral',
};

/** Věta „hlášení už je v tom stavu“ pro každý stav zvlášť — název stavu „Řeší se“ je sloveso (nález SPR-S3). */
export const UZ_VE_STAVU: Readonly<Record<StavHlaseni, string>> = {
  nove: 'Hlášení je už nové.',
  resi_se: 'Hlášení už se řeší.',
  vyreseno: 'Hlášení je už vyřešené.',
  zamitnuto: 'Hlášení je už zamítnuté.',
};

export const POPIS_STAVU: Readonly<Record<StavHlaseni, string>> = {
  nove: 'Nové',
  resi_se: 'Řeší se',
  vyreseno: 'Vyřešeno',
  zamitnuto: 'Zamítnuto',
};

export const POPIS_POHLEDU: Readonly<Record<PohledHlaseni, string>> = {
  otevrene: 'Otevřené',
  ...POPIS_STAVU,
  vse: 'Vše',
};

/** Prototyp H:43500: závada červeně, námět modře. */
export const STITEK_TYPU: Readonly<Record<TypHlaseni, string>> = {
  zavada: 'stitek--danger',
  namet: 'stitek--info',
};

/** Popisek typu i se zdrojem („Námět asistenta“, „Připomínka k návodu“ — prototyp H:43500). */
export function popisTypu(h: Pick<Hlaseni, 'typ' | 'zdroj' | 'typ_popis'>): string {
  if (h.zdroj === 'napoveda') return 'Připomínka k návodu';
  if (h.zdroj === 'asistent') return h.typ === 'zavada' ? 'Závada od asistenta' : 'Námět asistenta';
  return h.typ_popis;
}

/** Povolené přechody (02 C3; server je hlídá — 409). */
export const PRECHODY: Readonly<Record<StavHlaseni, readonly StavHlaseni[]>> = {
  nove: ['resi_se', 'vyreseno', 'zamitnuto'],
  resi_se: ['vyreseno', 'zamitnuto'],
  vyreseno: ['resi_se'],
  zamitnuto: ['resi_se'],
};

/** Proč nejde přepnout do stavu `na` — text vedle neaktivního tlačítka; undefined = jde. */
export function duvodPrechodu(z: StavHlaseni, na: StavHlaseni): string | undefined {
  if (PRECHODY[z].includes(na)) return undefined;
  if (z === na) return UZ_VE_STAVU[z];
  const mozne = PRECHODY[z].map((s) => POPIS_STAVU[s].toLowerCase()).join(', ');
  return `Ze stavu ${POPIS_STAVU[z].toLowerCase()} lze jen: ${mozne}.`;
}

export interface FiltrHlaseni {
  typ?: TypHlaseni;
  stav?: StavHlaseni | 'otevrene';
  zdroj?: ZdrojHlaseni;
  sekce?: string;
  jenMoje?: boolean;
  hledat?: string;
  razeni?: string;
  strana?: number;
  velikost?: number;
}

export function dotazHlaseni(f: FiltrHlaseni): string {
  const p = new URLSearchParams();
  if (f.typ) p.set('typ', f.typ);
  if (f.stav) p.set('stav', f.stav);
  if (f.zdroj) p.set('zdroj', f.zdroj);
  if (f.sekce) p.set('sekce', f.sekce);
  if (f.jenMoje) p.set('jen_moje', 'true');
  if (f.hledat?.trim()) p.set('hledat', f.hledat.trim());
  if (f.razeni) p.set('razeni', f.razeni);
  p.set('strana', String(f.strana ?? 1));
  p.set('velikost', String(f.velikost ?? 50));
  return `?${p.toString()}`;
}

export function useHlaseni(f: FiltrHlaseni, povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_HLASENI, 'seznam', f],
    queryFn: ({ signal }) => api.get<HlaseniSeznam>(`/hlaseni${dotazHlaseni(f)}`, { signal }),
    placeholderData: keepPreviousData,
    enabled: povoleno,
  });
}

/** Moje hlášení k aplikaci (Domů, drawer Nahlásit) — i bez spojení se obrazovka vykreslí, seznam jen chybí. */
export function useMojeHlaseni(povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_HLASENI, 'moje'],
    queryFn: ({ signal }) =>
      api.get<HlaseniSeznam>(
        `/hlaseni${dotazHlaseni({ jenMoje: true, velikost: MOJE_HLASENI_POCET })}`,
        {
          signal,
          bezHlaseniSite: true,
        },
      ),
    enabled: povoleno,
    retry: false,
  });
}

const enc = encodeURIComponent;

export function useHlaseniDetail(id: string | null | undefined) {
  return useQuery({
    queryKey: [...KLIC_HLASENI, 'detail', id],
    queryFn: ({ signal }) => api.get<Hlaseni>(`/hlaseni/${enc(id ?? '')}`, { signal }),
    enabled: Boolean(id),
  });
}

export const podatHlaseni = (telo: HlaseniVstup) => api.post<Hlaseni>('/hlaseni', telo);

export const resitHlaseni = (id: string, telo: ResitVstup) =>
  api.post<Hlaseni>(`/hlaseni/${enc(id)}/resit`, telo);

export function obnovitHlaseni(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({ queryKey: KLIC_HLASENI });
}
